/* eslint-disable max-len */
/* eslint-disable security/detect-object-injection */

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CardHeader from '../../../components/CardHeader';
import TopBarWithShadow from '../../../components/TopBar/TopBarWithShadow';
import ItemShimmer from '../../../components/Shimmer/itemShimmer';
import Toast from '../../../components/Toast';
import GlobalContext from '../../../components/Context/GloablContext';
import { dataService } from '../../../services/data.services';
import { GetBranchId } from '../../../CommonMethods/GetBranchId';

const getConfig = (type) => {
    const isSections = type === 'sections';

    return {
        dragIdPrefix: isSections ? 'section' : 'class',
        itemLabel: isSections ? 'section' : 'class',
        entityLabelPlural: isSections ? 'Sections' : 'Classes',
        activeTitle: isSections ? 'Active Sections' : 'Active Classes',
        secondaryTitle: isSections ? 'Inactive Sections' : 'Inactive Classes',
        emptyMessage: isSections ? 'No sections available in this section.' : 'No classes available in this section.',
        reorderHint: isSections
            ? 'Drag to reorder. Cannot move between Active and Inactive sections.'
            : 'Drag to reorder. Cannot move between Active and Inactive classes.',
        fetchEndpoint: isSections ? 'sections' : 'classes',
        saveEndpoint: isSections ? 'sections/reorder' : 'classes/reorder',
        successMessage: isSections ? 'Section order updated successfully' : 'Class order updated successfully',
        errorFetchMessage: isSections ? 'Unable to fetch sections.' : 'Unable to fetch classes.',
        errorSaveMessage: isSections ? 'Unable to save section order.' : 'Unable to save class order.',
        backLocation: isSections ? '/main-hub/manage-sections' : '/main-hub/classes',
        displayField: isSections ? 'section' : 'name',
        normalizeStatus: (status) => {
            if (isSections) {
                if (typeof status === 'boolean') {
                    return status ? 'active' : 'inactive';
                }

                const normalizedStatus = String(status || '').trim().toLowerCase();
                return normalizedStatus === 'active' || normalizedStatus === 'true' ? 'active' : 'inactive';
            }

            if (typeof status === 'boolean') {
                return status ? 'active' : 'inactive';
            }

            const normalizedStatus = String(status || '').trim().toLowerCase();
            return normalizedStatus === 'active' || normalizedStatus === 'true' ? 'active' : 'inactive';
        },
        secondaryStatus: 'inactive',
        saveStatusValue: (status) => {
            return status === 'active';
        },
    };
};

const sortClassList = (list = [], normalizeStatus) => [...list].sort((a, b) => {
    const aStatus = normalizeStatus(a?.status);
    const bStatus = normalizeStatus(b?.status);

    if (aStatus !== bStatus) {
        return aStatus === 'active' ? -1 : 1;
    }

    return Number(a?.display_order || 0) - Number(b?.display_order || 0);
});

const mapClassItem = (item, index, config) => ({
    ...item,
    name: item?.[config.displayField] || item?.name || '-',
    _dragId: item?._dragId || item?._id || item?.id || `${config.dragIdPrefix}-${Date.now()}-${index}`,
    _entityId: item?._id || item?.id || '',
    status: config.normalizeStatus(item?.status),
});

const SortableClassRow = ({ id, index, item }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="flex items-center justify-between p-2 border border-[#E6E8EA] rounded bg-white"
        >
            <div className="flex gap-3 min-w-0 w-full">
                <button
                    type="button"
                    className="bulk-add-drag-handle"
                    aria-label={`Reorder ${item?.name || 'item'} row ${index + 1}`}
                    {...attributes}
                    {...listeners}
                >
                    <span></span>
                    <span></span>
                    <span></span>
                </button>
                <div className="flex items-center gap-2 min-w-0 justify-between w-full">
                    <p className="truncate">{item?.name || '-'}</p>
                    <p className="text-[#667085] truncate">{item?.display_order}</p>
                </div>
            </div>
        </div>
    );
};

const ReorderSection = ({ title, items, emptyMessage, onDragEnd }) => {
    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: {
            distance: 8,
        },
    }));

    return (
        <>
            <div className="flex items-center justify-between mb-2 mt-4 mx-auto w-[45%]">
                <p className="text-[#031624] font-semibold">{title} ({items.length})</p>
            </div>
            <div className="border border-[#E6E8EA] rounded bg-[#F8FAFC] p-4  mx-auto w-[45%]">
                {items.length === 0 ? (
                    <div className="border border-dashed border-[#D0D5DD] rounded bg-white p-6 text-center text-[#667085]">
                        {emptyMessage}
                    </div>
                ) : (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={onDragEnd}
                    >
                        <SortableContext
                            items={items.map((item) => item._dragId)}
                            strategy={verticalListSortingStrategy}
                        >
                            <div className="flex flex-col gap-2">
                                {items.map((item, index) => (
                                    <SortableClassRow
                                        key={item._dragId}
                                        id={item._dragId}
                                        item={item}
                                        index={index}
                                    />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                )}
            </div>
        </>
    );
};

export default function ReorderClasses({ onClose = null, type = 'classes' }) {
    const navigate = useNavigate();
    const { setToastmessage, setToastErrorMessage } = useContext(GlobalContext);
    const config = getConfig(type);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [activeClasses, setActiveClasses] = useState([]);
    const [inactiveClasses, setInactiveClasses] = useState([]);
    const [initialActiveOrder, setInitialActiveOrder] = useState([]);
    const [initialInactiveOrder, setInitialInactiveOrder] = useState([]);

    const hasChanges = useMemo(() => {
        const hasListChanges = (items = [], initialOrder = []) => {
            if (items.length !== initialOrder.length) return true;
            return items.some((item, index) => item?._entityId !== initialOrder[index]);
        };

        return hasListChanges(activeClasses, initialActiveOrder)
            || hasListChanges(inactiveClasses, initialInactiveOrder);
    }, [activeClasses, inactiveClasses, initialActiveOrder, initialInactiveOrder]);

    const loadClasses = async () => {
        setLoading(true);
        setErrorMessage('');

        try {
            const branchId = await GetBranchId();
            const response = await dataService.GetAPI(`${config.fetchEndpoint}?branch_id=${branchId}`);
            const sortedClasses = sortClassList(
                response?.data?.data?.data || [],
                config.normalizeStatus
            ).map((item, index) => mapClassItem(item, index, config));

            const activeList = sortedClasses
                .filter((item) => item.status === 'active')
                .map((item, index) => ({ ...item, display_order: index + 1 }));
            const inactiveList = sortedClasses
                .filter((item) => item.status === config.secondaryStatus)
                .map((item, index) => ({ ...item, display_order: index + 1 }));

            setActiveClasses(activeList);
            setInactiveClasses(inactiveList);
            setInitialActiveOrder(activeList.map((item) => item._entityId));
            setInitialInactiveOrder(inactiveList.map((item) => item._entityId));
        } catch (error) {
            setErrorMessage(error?.response?.data?.message || config.errorFetchMessage);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadClasses();
    }, [type]);

    const createDragHandler = (items, setItems) => (event) => {
        const { active, over } = event;
        if (!active?.id || !over?.id || active.id === over.id) return;

        const oldIndex = items.findIndex((item) => item._dragId === active.id);
        const newIndex = items.findIndex((item) => item._dragId === over.id);
        if (oldIndex < 0 || newIndex < 0) return;

        const reorderedItems = arrayMove(items, oldIndex, newIndex)
            .map((item, index) => ({ ...item, display_order: index + 1 }));
        setItems(reorderedItems);
    };

    const handleSave = async () => {
        setSaving(true);
        setErrorMessage('');

        try {
            const branchId = await GetBranchId();
            const payload = {
                branch_id: branchId,
                items: [
                    ...activeClasses.map((item, index) => ({
                        _id: item._entityId,
                        display_order: index + 1,
                        status: config.saveStatusValue('active'),
                    })),
                    ...inactiveClasses.map((item, index) => ({
                        _id: item._entityId,
                        display_order: index + 1,
                        status: config.saveStatusValue(config.secondaryStatus),
                    })),
                ],
            };

            const response = await dataService.PatchAPI(payload, config.saveEndpoint);
            setToastmessage(response?.data?.message || config.successMessage);
            if (onClose) {
                onClose();
            } else {
                navigate(config.backLocation);
            }
        } catch (error) {
            const message = error?.response?.data?.message || config.errorSaveMessage;
            setErrorMessage(message);
            setToastErrorMessage(message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <CardHeader noPadding>
            <TopBarWithShadow
                Tittle={`Reorder ${config.entityLabelPlural}`}
                isback={true}
                backLocation={config.backLocation}
                rightContent={
                    <div className="flex items-center">
                        <button
                            type="button"
                            className="cancl-btn"
                            onClick={() => {
                                if (onClose) {
                                    onClose();
                                } else {
                                    navigate(config.backLocation);
                                }
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="actn-btn"
                            disabled={!hasChanges || saving}
                            onClick={handleSave}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                }
            />
            <CardHeader body>
                {errorMessage !== '' && (
                    <Toast
                        message={errorMessage}
                        type="error"
                        setToastmessage={setErrorMessage}
                    />
                )}
                {loading ? (
                    <ItemShimmer />
                ) : (
                    <div className="flex flex-col">
                        <p className="text-sm text-[#667085] border mx-auto w-[45%] p-2">
                            {config.reorderHint}
                        </p>
                        <ReorderSection
                            title={config.activeTitle}
                            items={activeClasses}
                            emptyMessage={config.emptyMessage}
                            onDragEnd={createDragHandler(activeClasses, setActiveClasses)}
                        />
                        <ReorderSection
                            title={config.secondaryTitle}
                            items={inactiveClasses}
                            emptyMessage={config.emptyMessage}
                            onDragEnd={createDragHandler(inactiveClasses, setInactiveClasses)}
                        />
                    </div>
                )}
            </CardHeader>
        </CardHeader>
    );
}
